import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface KeywordRanking {
  id: string
  keyword: string
  position: number
  previous_position: number
  change: number
  volume: number
  difficulty: number
  url: string
  traffic: number
}

interface TechnicalIssue {
  id: string
  issue: string
  count: number
  severity: string
  impact: string
  status: string
}

interface Statistics {
  organic_traffic: number
  keywords_ranking: number
  top_10_keywords: number
  avg_position: number
  domain_authority: number
  backlinks: number
}

export default function SEOPerformancePage() {
  const [keywords, setKeywords] = useState<KeywordRanking[]>([])
  const [issues, setIssues] = useState<TechnicalIssue[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [keywordsRes, issuesRes, statsRes] = await Promise.all([
        fetch('/api/seoperformance/keywords'),
        fetch('/api/seoperformance/issues'),
        fetch('/api/seoperformance/statistics')
      ])
      if (keywordsRes.ok) setKeywords(await keywordsRes.json())
      if (issuesRes.ok) setIssues(await issuesRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching SEO performance data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const getSeverityBadge = (severity: string) => {
    if (severity === 'high') return 'bg-red-100 text-red-800'
    if (severity === 'medium') return 'bg-yellow-100 text-yellow-800'
    return 'bg-green-100 text-green-800'
  }

  const getStatusBadge = (status: string) => {
    if (status === 'fixed') return 'bg-green-100 text-green-800'
    if (status === 'in_progress') return 'bg-blue-100 text-blue-800'
    return 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">SEO Performance</h1>
          <p className="mt-2 text-gray-600">Phase 247 - Search engine optimization analytics</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Organic Traffic</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(statistics.organic_traffic)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Keywords Ranking</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.keywords_ranking}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Top 10 Keywords</p>
              <p className="text-2xl font-bold text-green-600">{statistics.top_10_keywords}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Position</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.avg_position}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Domain Authority</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.domain_authority}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Backlinks</p>
              <p className="text-2xl font-bold text-indigo-600">{formatNumber(statistics.backlinks)}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Keyword Rankings</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Keyword</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Change</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Volume</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Difficulty</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Traffic</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {keywords.map((keyword) => (
                  <tr key={keyword.id}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{keyword.keyword}</div>
                      <div className="text-xs text-gray-500">{keyword.url}</div>
                    </td>
                    <td className="px-6 py-4 font-bold text-blue-600">#{keyword.position}</td>
                    <td className={`px-6 py-4 font-medium ${keyword.change > 0 ? 'text-green-600' : keyword.change < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                      {keyword.change > 0 ? '+' : ''}{keyword.change}
                    </td>
                    <td className="px-6 py-4 text-gray-500">{formatNumber(keyword.volume)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-16 h-2 bg-gray-200 rounded-full mr-2">
                          <div className={`h-2 rounded-full ${keyword.difficulty > 60 ? 'bg-red-500' : keyword.difficulty > 40 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{width: `${keyword.difficulty}%`}}></div>
                        </div>
                        <span className="text-sm">{keyword.difficulty}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-green-600 font-medium">{formatNumber(keyword.traffic)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Technical SEO Issues</h2>
          </div>
          <div className="p-6 space-y-4">
            {issues.map((issue) => (
              <div key={issue.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{issue.issue}</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${getSeverityBadge(issue.severity)}`}>{issue.severity}</span>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(issue.status)}`}>{issue.status}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Affected: {issue.count} pages</span>
                  <span className="text-blue-600">Impact: {issue.impact}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
